import axios from 'axios'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'

export interface AutofillLocation {
  id: number
  city: number | null
  city_details: { id: number; city_name?: string } | null
  country: number | null
  country_details: { id: number; country_name?: string } | null
  location_type: string
  region: string | null
  us_state: string | null
  location_name: string
}

export interface AutofillData {
  url: string
  status_open: boolean
  role_name: string
  description: string | null
  qualifications: string | null
  seniority_level: string
  employer: number | null
  employer_details: {
    id: number
    name: string
    url: string | null
    logo: string | null
  } | null
  microsoft_role_id: string
  is_remote: boolean
  locations: number[]
  locations_details: AutofillLocation[]
  suggested_locations: Array<{
    type: 'suggestion'
    label: string
    create_data: Record<string, unknown>
  }>
}

export interface AutofillFromUrlResult {
  hasError: boolean
  errorMessage: string | null
  errorType?: 'not_unique' | 'job_closed' | 'scrape_failed' | 'validation_error'
  roleId?: number
  autofillData?: AutofillData
}

export const autofillFromUrl = async (url: string): Promise<AutofillFromUrlResult> => {
  if (!url || url.trim() === '') {
    return { hasError: false, errorMessage: null }
  }

  try {
    const response = await api.get(API_ROUTES.AUTOFILL_FROM_URL, {
      params: { url: url.trim() }
    })

    if (!response.data?.autofill_data) {
      return { hasError: true, errorMessage: 'No data was returned for this URL', errorType: 'scrape_failed' }
    }

    return {
      hasError: false,
      errorMessage: null,
      autofillData: response.data.autofill_data
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data
      const errorType: AutofillFromUrlResult['errorType'] = responseData?.error_type

      if (errorType === 'not_unique' && responseData?.role_id) {
        return {
          hasError: true,
          errorType: 'not_unique',
          roleId: responseData.role_id,
          errorMessage: 'Job posting already exists'
        }
      }

      if (errorType === 'job_closed') {
        return {
          hasError: true,
          errorType: 'job_closed',
          errorMessage: 'This job posting appears to be closed'
        }
      }

      if (errorType === 'scrape_failed') {
        return {
          hasError: true,
          errorType: 'scrape_failed',
          errorMessage: responseData?.message || 'Unable to extract job data from this URL'
        }
      }

      if (errorType === 'validation_error') {
        return {
          hasError: true,
          errorType: 'validation_error',
          errorMessage: responseData?.message || 'Invalid URL'
        }
      }
    }

    console.error('Unexpected error during URL autofill:', error)
    return {
      hasError: true,
      errorType: 'scrape_failed',
      errorMessage: 'An unexpected error occurred. Please try again or fill the form manually.'
    }
  }
}
